import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./AllergyWorkupSection.css";
import PulmonologySaveButton from "../PulmonologySaveButton/PulmonologySaveButton";

const tests = ["histamine", "saline", "dustMite", "grassPollen", "treePollen", "molds", "cat", "dog", "cockroach", "food"].map((key) => [key, "0", "notTested"]);
const initialIgE = {
  total: { value: "0", detail: "" },
  perennial: { value: "0", detail: "" },
  food: { value: "0", detail: "" },
  molecular: { value: "0", detail: "" },
};

export default function AllergyWorkupSection({ record, saveRecord, saving, canSave }) {
  const { t } = useTranslation("pulmonology");
  const [results, setResults] = useState(Object.fromEntries(tests.map(([key, size, status]) => [key, { size, status }])));
  const [igeResults, setIgeResults] = useState(initialIgE);
  const [editingTest, setEditingTest] = useState(null);
  const [editingIgE, setEditingIgE] = useState(null);
  const [syncedAllergens, setSyncedAllergens] = useState(null);
  const persistedRecord = Array.isArray(record) ? record[0] : record;

  useEffect(() => {
    if (!persistedRecord) return;
    if (Array.isArray(persistedRecord.prickTests)) {
      setResults(Object.fromEntries(persistedRecord.prickTests.map((test) => [test.name, {
        size: String(test.size),
        status: test.status,
      }])));
    }
    if (Array.isArray(persistedRecord.igeResults)) {
      setIgeResults(Object.fromEntries(persistedRecord.igeResults.map((result) => [result.name, {
        value: result.value,
        detail: result.detail,
      }])));
    }
  }, [persistedRecord]);

  const saveAllergyWorkup = () => saveRecord?.("allergy-workup", {
    prickTests: tests.map(([name]) => ({
      name,
      size: Number(results[name]?.size) || 0,
      status: results[name]?.status || "negative",
    })),
    igeResults: Object.entries(igeResults).map(([name, result]) => ({
      name,
      value: result.value,
      detail: result.detail ?? t(`allergy.igeDetails.${result.detailKey}`),
    })),
  }, persistedRecord?.id);
  const positiveAllergens = tests.filter(([nameKey]) => results[nameKey]?.status === "positive").map(([nameKey]) => t(`allergy.tests.${nameKey}`));

  return (
    <div className="pulmo-form">
      <div className="pulmo-exam-head"><strong>{t("allergy.title")}</strong><button type="button" className="pulmo-outline-button" onClick={() => setSyncedAllergens(positiveAllergens)}>{syncedAllergens ? t("allergySync.syncedCount", { count: syncedAllergens.length }) : t("allergy.sync")}</button></div>
      <div className="pulmo-allergy-ige-grid">{Object.entries(igeResults).map(([key, result]) => <div className="pulmo-allergy-metric" key={key}>
        {editingIgE === key ? <div className="pulmo-allergy-edit">
          <strong>{t(`allergy.ige.${key}`)}</strong>
          <input aria-label={t(`allergy.ige.${key}`)} value={result.value} onChange={(event) => setIgeResults((items) => ({ ...items, [key]: { ...items[key], value: event.target.value } }))} />
          <input aria-label={t("allergy.editDetail")} value={result.detail ?? t(`allergy.igeDetails.${result.detailKey}`)} onChange={(event) => setIgeResults((items) => ({ ...items, [key]: { ...items[key], detail: event.target.value } }))} onBlur={() => setEditingIgE(null)} onKeyDown={(event) => event.key === "Enter" && setEditingIgE(null)} />
        </div> : <button type="button" className="pulmo-allergy-metric__display" onClick={() => setEditingIgE(key)}>
          <small>{t(`allergy.ige.${key}`)}</small><strong>{result.value}{key === "total" ? <span> kU/L</span> : ` ${t("allergy.igeDetails.tested")}`}</strong><span>{result.detail ?? t(`allergy.igeDetails.${result.detailKey}`)}</span>
        </button>}
      </div>)}</div>
      <div className="pulmo-allergy-grid">{tests.map(([nameKey]) => {
        const result = results[nameKey];
        const statusKey = result.status;
        const status = statusKey === "controlPositive" ? t("allergy.controlPositive") : statusKey === "controlNegative" ? t("allergy.controlNegative") : t(`common.${statusKey}`);
        return <div className={`pulmo-allergy-test${statusKey === "positive" ? " is-positive" : ""}`} key={nameKey}>
          {editingTest === nameKey ? <div className="pulmo-allergy-edit">
            <strong>{t(`allergy.tests.${nameKey}`)}</strong>
            <label><input type="number" min="0" value={result.size} aria-label={t("allergy.whealSize")} onChange={(event) => setResults((items) => ({ ...items, [nameKey]: { ...items[nameKey], size: event.target.value } }))} /><span>mm</span></label>
            <select aria-label={t("allergy.result")} value={statusKey} onChange={(event) => {
              setResults((items) => ({ ...items, [nameKey]: { ...items[nameKey], status: event.target.value } }));
              setSyncedAllergens(null);
            }} onBlur={() => setEditingTest(null)}>
              <option value="notTested">{t("common.notTested")}</option><option value="positive">{t("common.positive")}</option><option value="negative">{t("common.negative")}</option>
              {nameKey === "histamine" && <option value="controlPositive">{t("allergy.controlPositive")}</option>}
              {nameKey === "saline" && <option value="controlNegative">{t("allergy.controlNegative")}</option>}
            </select>
          </div> : <button type="button" className="pulmo-allergy-test__display" aria-label={t("allergy.editTest", { name: t(`allergy.tests.${nameKey}`) })} onClick={() => setEditingTest(nameKey)}>
            <strong>{t(`allergy.tests.${nameKey}`)}</strong><span>{result.size} mm</span><small>{status}</small>
          </button>}
        </div>;
      })}</div>
      {syncedAllergens && <div className="pulmo-allergy-sync-status" role="status">{syncedAllergens.length ? syncedAllergens.join(", ") : t("allergySync.noneToSync")}</div>}
      <div className="pulmo-actions">
        <PulmonologySaveButton onClick={saveAllergyWorkup} saving={saving} disabled={!canSave} />
      </div>
    </div>
  );
}
